import { useEffect, useState, useMemo, useCallback } from "react"; // ✅ Adicionar useMemo
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useRenterDashboardContext } from "../renter-dashboard/useRenterDashboardContext";
import { PaymentInfo } from "@/types/payment.type";
import { getRenterPayments, getRenterPaymentsAsaas } from "@/common/services/payments.api";

export function usePaymentsTable() {
  const { notifySuccess, notifyError } = useToastContext();
  const { renter } = useRenterDashboardContext();

  const [payments, setPayments] = useState<PaymentInfo[]>([]);
  const [showFines, setShowFines] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [modalPayment, setModalPayment] = useState(false);

  const loadPayments = useCallback(async (renterId: string) => {
    if (!renterId || !renter.cpf) return;

    setIsLoading(true);
    try {
      const { data, ok } = await getRenterPayments(renterId);
      if (ok) {
        const sortedPayments = data.sort((a, b) => {
          const dateB = new Date(b.dueDate!);
          const dateA = new Date(a.dueDate!);
          return dateB.getTime() - dateA.getTime();
        });

        setPayments(sortedPayments as unknown as PaymentInfo[]);
      }
    } catch (error) {
      notifyError("Erro ao carregar pagamentos");
      console.error("Erro ao carregar pagamentos:", error);
    } finally {
      setIsLoading(false);
    }
  }, [renter.cpf, notifyError]);

  const filteredPayments = useMemo(() => {
    let filtered = payments;

    // Filtro por status
    if (statusFilter) {
      if (statusFilter === "DUNNING") {
        // ✅ Verificar se status existe antes de usar includes
        filtered = filtered.filter(payment =>
          payment.status && payment.status.includes("DUNNING")
        );
      } else if (statusFilter === "RECEIVED") {
        // ✅ Verificar se status existe antes de usar includes
        filtered = filtered.filter(payment =>
          payment.status && ["RECEIVED", "CONFIRMED", "RECEIVED_IN_CASH"].includes(payment.status)
        );
      } else {
        // ✅ Para outros status únicos - verificação já existe
        filtered = filtered.filter(payment => payment.status === statusFilter);
      }
    }

    // Filtro por multas
    if (showFines) {
      filtered = filtered.filter(payment => {
        const isTrafficFine =
          payment.planId?.match(/\b(multa|fine)\b/i) ||
          /AUTO|INFRAÇÃO|AUTO DE INFRAÇÃO/i.test(payment.description);
        // ✅ Verificar se status existe antes de usar includes
        return isTrafficFine && payment.status && ['PENDING', 'OVERDUE'].includes(payment.status);
      });
    }

    return filtered;
  }, [payments, statusFilter, showFines]);
  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    setShowFines(false);
  };

  const handleShowFines = () => {
    setShowFines((prevShowFines) => !prevShowFines);
    setStatusFilter('');
  };

  const handleSyncAsaas = async () => {
    try {
      setIsLoading(true);
      const { ok } = await getRenterPaymentsAsaas(renter._id!);

      if (ok) {
        notifySuccess('Sincronização com Asaas realizada com sucesso!');
        if (renter._id) await loadPayments(renter._id);
      }

    } catch (error) {
      notifyError((error as Error).message);
      console.error("Erro ao sincronizar com Asaas:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ useEffect simplificado - apenas carrega dados sem filtrar
  useEffect(() => {
    async function loadPayments(renterId: string) {
      if (!renterId || !renter.cpf) return;

      setIsLoading(true);
      try {
        const { data, ok } = await getRenterPayments(renterId);
        if (ok) {
          // ✅ Apenas ordenar, não filtrar aqui
          const sortedPayments = data.sort((a, b) => {
            const dateB = new Date(b.dueDate!);
            const dateA = new Date(a.dueDate!);
            return dateB.getTime() - dateA.getTime();
          });

          setPayments(sortedPayments as unknown as PaymentInfo[]);
        }
      } catch (error) {
        notifyError("Erro ao carregar pagamentos");
        console.error("Erro ao carregar pagamentos:", error);
      } finally {
        setIsLoading(false);
      }
    }

    setPayments([]);
    if (renter._id) loadPayments(renter._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renter._id, renter.cpf]);

  useEffect(() => {
    setPayments([]);
    if (renter._id) {
      loadPayments(renter._id);
    }
  }, [renter._id, loadPayments]);

  return {
    renter,
    payments: filteredPayments, // ✅ Retornar os dados filtrados
    showFines,
    isLoading,
    statusFilter,
    modalPayment,
    setModalPayment,
    handleStatusChange,
    handleShowFines,
    handleSyncAsaas,
  };
}